import {
  type BulkIpDayUpdate,
  type BulkIpDayUpdateRequest,
  catchUpSchedule,
  type PlansSyncResponse,
  type SubscribeRequest,
  type SubscribeResponse,
  type SyncedInstantiatedPlan,
  type SyncedInstantiatedPlanDay,
} from '@soulhx/fs-common';
import {
  db,
  type IpDayField,
  type ItemSyncStatus,
  type LocalInstantiatedPlanDay,
  type LocalPlan,
} from '../model/db.js';
import { triggerGlobalSync } from './GlobalSyncMiddleware.js';
import { isItemGoneError, syncFetch, syncPull } from './syncFetch.js';
import { localToday, nextSyncCursor, pullSince, toUtcIso } from './timestamps.js';

/*
 * Sync for reading plans. Three collections, pulled in dependency order:
 *
 * - plans: every plan *version* visible to the user (their own, admin
 *   plans, and any plan they're subscribed to); pull-only, since
 *   authoring is online-only. Visibility is decided by the server, which
 *   reports a plan that's no longer visible (deleted, superseded and not
 *   subscribed, an admin draft, ...) as a tombstone.
 * - instantiatedPlans: the user's subscriptions
 * - instantiatedPlanDays: the days of those subscriptions; never
 *   tombstoned individually, they go when their instantiated plan does
 *
 * Local changes (all offline-capable) are limited to the user's own
 * subscriptions: marking days complete, catching up, and unsubscribing.
 * Day changes only push the fields that changed (`dirtyFields`), so a
 * mark-complete on one device and a catch-up (which moves dates) on
 * another both survive. Subscribing, and anything to do with authoring
 * plans, is online-only.
 */

export const PLAN_SYNC_POLL_INTERVAL_MS = 120_000;
export const PLAN_SYNC_KEY = 'plans';

const PLAN_SYNC_PATH = '/read/pl/sync';
const IP_PATH = '/read/pl/ip';

// well under the API's limit (500), and keeps each request comfortably
// within the 64kb body limit browsers impose on `keepalive` requests
const BULK_PUSH_CHUNK_SIZE = 400;
const PLANS_CURSOR_KEY = PLAN_SYNC_KEY;
const IPS_CURSOR_KEY = 'instantiatedPlans';
const IP_DAYS_CURSOR_KEY = 'instantiatedPlanDays';

// whether the user was an admin as of the last plans pull, stored as the
// string 'true'/'false' in the syncMeta row's `lastSyncedAt` - see pullPlans
const PLANS_IS_ADMIN_KEY = 'plans:isAdmin';

// see SettingSync.ts - passing `since` at all (even this far back) routes
// the server to the sync-oriented delta endpoint on the very first pull
const EPOCH = new Date(0).toISOString();

const isUnsynced = (status: ItemSyncStatus | undefined): boolean => status !== undefined && status !== 'synced';

const logSyncError = (context: string, error: unknown) => {
  // eslint-disable-next-line no-console
  console.error(`plan sync: ${context}`, error);
};

const getCursor = async (key: string): Promise<string> => (await db.syncMeta.get(key))?.lastSyncedAt ?? EPOCH;

const fetchPlansSince = (since: string) =>
  syncPull<PlansSyncResponse>(`${PLAN_SYNC_PATH}/plans?since=${encodeURIComponent(pullSince(since))}`);

/**
 * Pulls changed plans. Which plans are visible depends on whether the user
 * is an admin (admins also see admin drafts), and an initial pull leaves
 * out plans that aren't visible rather than sending tombstones for them -
 * so when the user's admin status changes, any plans pulled under the old
 * status can't be trusted: the table is cleared and pulled from scratch.
 */
export const pullPlans = async (): Promise<void> => {
  let since = await getCursor(PLANS_CURSOR_KEY);
  let { data: response, horizon } = await fetchPlansSince(since);

  const storedIsAdmin = (await db.syncMeta.get(PLANS_IS_ADMIN_KEY))?.lastSyncedAt;
  const adminStatusChanged = storedIsAdmin !== undefined && storedIsAdmin !== String(response.isAdmin);
  if (adminStatusChanged && since !== EPOCH) {
    since = EPOCH;
    ({ data: response, horizon } = await fetchPlansSince(since));
  }

  let maxLastUpdate = since;

  await db.transaction('rw', db.plans, db.syncMeta, async () => {
    if (adminStatusChanged) await db.plans.clear();

    for (const { deleted, ...serverPlan } of response.plans) {
      const updateDate = toUtcIso(serverPlan.updateDate);
      if (updateDate > maxLastUpdate) {
        maxLastUpdate = updateDate;
      }

      if (deleted) {
        await db.plans.delete(serverPlan.planInstanceId);
        continue;
      }

      const local = await db.plans.get(serverPlan.planInstanceId);
      if (local?.updateDate === updateDate) continue;

      const toStore: LocalPlan = { ...serverPlan, updateDate };
      await db.plans.put(toStore);
    }

    await db.syncMeta.put({ itemId: PLANS_CURSOR_KEY, lastSyncedAt: nextSyncCursor(since, maxLastUpdate, horizon) });
    await db.syncMeta.put({ itemId: PLANS_IS_ADMIN_KEY, lastSyncedAt: String(response.isAdmin) });
  });
};

export const pullInstantiatedPlans = async (): Promise<void> => {
  const since = await getCursor(IPS_CURSOR_KEY);
  const { data: serverIps, horizon } = await syncPull<SyncedInstantiatedPlan[]>(
    `${PLAN_SYNC_PATH}/ip?since=${encodeURIComponent(pullSince(since))}`
  );

  let maxLastUpdate = since;

  await db.transaction('rw', db.instantiatedPlans, db.instantiatedPlanDays, db.syncMeta, async () => {
    for (const { deleted, ...serverIp } of serverIps) {
      const updateDate = toUtcIso(serverIp.updateDate);
      if (updateDate > maxLastUpdate) {
        maxLastUpdate = updateDate;
      }

      // unsubscribed (here or on another device): the days go with it,
      // including any pending changes to them, which the server would
      // reject anyway
      if (deleted) {
        await db.instantiatedPlans.delete(serverIp.id);
        await db.instantiatedPlanDays.where('instantiatedPlanId').equals(serverIp.id).delete();
        continue;
      }

      const local = await db.instantiatedPlans.get(serverIp.id);
      if (local && isUnsynced(local.syncStatus)) continue;

      if (local?.updateDate === updateDate) continue;

      await db.instantiatedPlans.put({ ...serverIp, updateDate, syncStatus: 'synced' });
    }

    await db.syncMeta.put({ itemId: IPS_CURSOR_KEY, lastSyncedAt: nextSyncCursor(since, maxLastUpdate, horizon) });
  });
};

export const pullInstantiatedPlanDays = async (): Promise<void> => {
  const since = await getCursor(IP_DAYS_CURSOR_KEY);
  const { data: serverDays, horizon } = await syncPull<SyncedInstantiatedPlanDay[]>(
    `${PLAN_SYNC_PATH}/ipd?since=${encodeURIComponent(pullSince(since))}`
  );

  let maxLastUpdate = since;

  await db.transaction('rw', db.instantiatedPlans, db.instantiatedPlanDays, db.syncMeta, async () => {
    for (const serverDay of serverDays) {
      const updateDate = toUtcIso(serverDay.updateDate);
      if (updateDate > maxLastUpdate) {
        maxLastUpdate = updateDate;
      }

      const local = await db.instantiatedPlanDays.get(serverDay.id);
      if (local && isUnsynced(local.syncStatus)) continue;

      if (local?.updateDate === updateDate) continue;

      const parent = await db.instantiatedPlans.get(serverDay.instantiatedPlanId);
      if (parent?.syncStatus === 'pendingDelete') continue;

      const toStore: LocalInstantiatedPlanDay = { ...serverDay, updateDate, syncStatus: 'synced' };
      await db.instantiatedPlanDays.put(toStore);
    }

    await db.syncMeta.put({ itemId: IP_DAYS_CURSOR_KEY, lastSyncedAt: nextSyncCursor(since, maxLastUpdate, horizon) });
  });
};

const deleteIpLocally = async (ipId: string): Promise<void> => {
  await db.transaction('rw', db.instantiatedPlans, db.instantiatedPlanDays, async () => {
    await db.instantiatedPlans.delete(ipId);
    await db.instantiatedPlanDays.where('instantiatedPlanId').equals(ipId).delete();
  });
};

const pushPendingUnsubscribes = async (keepalive?: boolean): Promise<void> => {
  const pending = await db.instantiatedPlans.filter((ip) => ip.syncStatus === 'pendingDelete').toArray();

  for (const ip of pending) {
    try {
      await syncFetch<{ success: boolean }>({ method: 'DELETE', path: `${IP_PATH}/${ip.id}`, keepalive });
      await deleteIpLocally(ip.id);
    } catch (error) {
      // already gone server-side (e.g. unsubscribed on another device)
      if (isItemGoneError(error)) {
        await deleteIpLocally(ip.id);
        continue;
      }

      logSyncError(`failed to unsubscribe ${ip.id}`, error);
    }
  }
};

const toBulkUpdate = (day: LocalInstantiatedPlanDay): BulkIpDayUpdate => {
  const update: BulkIpDayUpdate = { id: day.id };
  if (day.dirtyFields?.includes('completed')) update.completed = day.completed;
  if (day.dirtyFields?.includes('scheduledDate')) update.scheduledDate = day.scheduledDate;
  return update;
};

/**
 * After a successful push, a day is only marked synced if it hasn't
 * changed again while the push was in flight; any field whose local value
 * no longer matches what was pushed stays dirty, to go out next time.
 */
const markDaysPushed = async (pushed: BulkIpDayUpdate[], serverDays: SyncedInstantiatedPlanDay[]) => {
  const serverById = new Map(serverDays.map((d) => [d.id, d]));

  await db.transaction('rw', db.instantiatedPlanDays, async () => {
    for (const update of pushed) {
      const current = await db.instantiatedPlanDays.get(update.id);
      if (!current) continue;

      const stillDirty = (current.dirtyFields ?? []).filter((field) => current[field] !== update[field]);
      if (stillDirty.length > 0) {
        await db.instantiatedPlanDays.update(update.id, { dirtyFields: stillDirty });
        continue;
      }

      const server = serverById.get(update.id);
      await db.instantiatedPlanDays.update(update.id, {
        syncStatus: 'synced',
        dirtyFields: undefined,
        ...(server ? { updateDate: toUtcIso(server.updateDate) } : {}),
      });
    }
  });
};

const pushPendingDayUpdates = async (keepalive?: boolean): Promise<void> => {
  const pending = await db.instantiatedPlanDays.filter((d) => isUnsynced(d.syncStatus)).toArray();
  if (pending.length === 0) return;

  const byIp = new Map<string, LocalInstantiatedPlanDay[]>();
  for (const day of pending) {
    const days = byIp.get(day.instantiatedPlanId);
    if (days) days.push(day);
    else byIp.set(day.instantiatedPlanId, [day]);
  }

  for (const [ipId, days] of byIp) {
    const ip = await db.instantiatedPlans.get(ipId);
    if (!ip || ip.syncStatus === 'pendingDelete') continue;

    for (let i = 0; i < days.length; i += BULK_PUSH_CHUNK_SIZE) {
      const chunk = days.slice(i, i + BULK_PUSH_CHUNK_SIZE).map(toBulkUpdate);
      const body: BulkIpDayUpdateRequest = { days: chunk };

      try {
        const serverDays = await syncFetch<SyncedInstantiatedPlanDay[]>({
          method: 'PUT',
          path: `${IP_PATH}/d/bulk`,
          body,
          keepalive,
        });
        await markDaysPushed(chunk, serverDays);
      } catch (error) {
        // every day in a chunk belongs to the same subscription, so this
        // means the subscription is gone server-side (e.g. unsubscribed on
        // another device) - pending changes to it can never be applied
        if (isItemGoneError(error)) {
          await deleteIpLocally(ipId);
          break;
        }

        // left pending, to be retried on the next cycle
        logSyncError(`failed to push days for ${ipId}`, error);
        break;
      }
    }
  }
};

/**
 * Pushes local changes: unsubscribes first (so changes to days of a
 * subscription being removed aren't pushed for nothing), then day changes.
 */
export const pushPendingPlanData = async (options?: { keepalive?: boolean }): Promise<void> => {
  const keepalive = options?.keepalive;
  await pushPendingUnsubscribes(keepalive);
  await pushPendingDayUpdates(keepalive);
};

/**
 * Pushes any pending local changes, then pulls plans, subscriptions, and
 * their days, in that order - so a day always arrives after the
 * subscription (and plan) it belongs to.
 */
export const syncPlans = async (): Promise<void> => {
  await pushPendingPlanData();
  await pullPlans();
  await pullInstantiatedPlans();
  await pullInstantiatedPlanDays();
};

type IpDayChanges = Partial<Pick<LocalInstantiatedPlanDay, IpDayField>>;

/**
 * Applies changes to a day (within the caller's transaction), recording
 * which fields actually changed so only those are pushed
 */
const applyDayChanges = async (day: LocalInstantiatedPlanDay, changes: IpDayChanges): Promise<void> => {
  const changedFields = (Object.keys(changes) as IpDayField[]).filter(
    (field) => changes[field] !== undefined && changes[field] !== day[field]
  );
  if (changedFields.length === 0) return;

  const dirtyFields = Array.from(new Set([...(day.dirtyFields ?? []), ...changedFields]));
  await db.instantiatedPlanDays.update(day.id, { ...changes, dirtyFields, syncStatus: 'pendingUpdate' });
};

/**
 * Marks a day complete/incomplete (or moves it); works offline. Changes to
 * a subscription that's being unsubscribed are ignored.
 */
export const queueLocalIpDayUpdate = async (dayId: string, changes: IpDayChanges): Promise<void> => {
  await db.transaction('rw', db.instantiatedPlans, db.instantiatedPlanDays, async () => {
    const day = await db.instantiatedPlanDays.get(dayId);
    if (!day) return;

    const ip = await db.instantiatedPlans.get(day.instantiatedPlanId);
    if (!ip || ip.syncStatus === 'pendingDelete') return;

    await applyDayChanges(day, changes);
  });

  triggerGlobalSync(PLAN_SYNC_KEY);
};

/**
 * "Catches up" a subscription from a given day; works offline. See
 * fs-common's `catchUpSchedule` for exactly what changes.
 *
 * @param fromDate The (scheduled) date of the day to catch up from
 * @param markPrevAsRead Whether days before it should be marked as read
 */
export const queueLocalCatchUp = async (ipId: string, fromDate: string, markPrevAsRead: boolean): Promise<void> => {
  await db.transaction('rw', db.plans, db.instantiatedPlans, db.instantiatedPlanDays, async () => {
    const ip = await db.instantiatedPlans.get(ipId);
    if (!ip || ip.syncStatus === 'pendingDelete') return;

    const plan = await db.plans.get(ip.planInstanceId);
    if (!plan) throw new Error(`plan ${ip.planInstanceId} for subscription ${ipId} isn't synced`);

    const sequenceByPlanDay = new Map(plan.days.map((pd) => [pd.id, pd.sequenceNum]));
    const days = (await db.instantiatedPlanDays.where('instantiatedPlanId').equals(ipId).toArray()).sort(
      (a, b) => (sequenceByPlanDay.get(a.planDayId) ?? 0) - (sequenceByPlanDay.get(b.planDayId) ?? 0)
    );
    const daysById = new Map(days.map((d) => [d.id, d]));

    const updates = catchUpSchedule({
      days,
      fromDate,
      today: localToday(),
      markPrevAsRead,
      includeWeekends: plan.includeWeekends,
    });
    for (const { id, ...changes } of updates) {
      await applyDayChanges(daysById.get(id)!, changes);
    }
  });

  triggerGlobalSync(PLAN_SYNC_KEY);
};

/**
 * Unsubscribes from a plan; works offline. The subscription is hidden
 * immediately, and removed (with its days) once the server confirms.
 */
export const queueLocalUnsubscribe = async (ipId: string): Promise<void> => {
  await db.instantiatedPlans.update(ipId, { syncStatus: 'pendingDelete' });
  triggerGlobalSync(PLAN_SYNC_KEY);
};

/**
 * Subscribes to a plan. Online-only (the server builds the schedule, and
 * errors need to reach the user), so errors propagate to the caller; the
 * server's response is written straight into dexie.
 *
 * @param startDate The user's local date for the first day (`yyyy-MM-dd`)
 * @returns The new subscription's ID
 */
export const subscribeToPlan = async (planInstanceId: string, startDate: string = localToday()): Promise<string> => {
  const body: SubscribeRequest = { planInstanceId, startDate };
  const { ip, days } = await syncFetch<SubscribeResponse>({ method: 'POST', path: IP_PATH, body });

  await db.transaction('rw', db.instantiatedPlans, db.instantiatedPlanDays, async () => {
    await db.instantiatedPlans.put({
      id: ip.id,
      planInstanceId: ip.planInstanceId,
      updateDate: toUtcIso(ip.updateDate),
      syncStatus: 'synced',
    });
    await db.instantiatedPlanDays.bulkPut(
      days.map((d) => ({ ...d, updateDate: toUtcIso(d.updateDate), syncStatus: 'synced' as const }))
    );
  });

  triggerGlobalSync(PLAN_SYNC_KEY);
  return ip.id;
};
